'use client';

import { forwardRef, ForwardedRef, useId } from 'react';
import classNames from 'classnames';

import { Label } from '../Label';
import { Error } from '../Error';
import { Helper } from '../Helper';
import './css/Select.scss';

export interface SelectProps
  extends React.SelectHTMLAttributes<HTMLSelectElement> {
  id?: string;
  error?: boolean | string;
  errorDescribedBy?: string;
  helper?: string;
  label?: string;
  disabled?: boolean;
  defaultValue?: string;
  options: { disabled?: boolean; label: string; value?: string }[];
  isErrorAnnounced?: boolean;
}

export const Select = forwardRef(
  (props: SelectProps, ref: ForwardedRef<HTMLSelectElement>) => {
    const {
      className,
      id,
      error,
      helper,
      label,
      disabled,
      defaultValue,
      errorDescribedBy,
      options,
      isErrorAnnounced = true,
      ...spreadProps
    } = props;

    const customSelectId = `${useId()}-select`;
    const uniqueSelectId = id || customSelectId;
    const uniqueErrorId = `${uniqueSelectId}-error`;
    const uniqueHelperId = `${uniqueSelectId}-helper`;

    const errorWithNoMessage = typeof error === 'boolean';

    const describedBy = (() => {
      if (helper || error || errorDescribedBy) {
        const errorDescribedById = errorDescribedBy || uniqueErrorId;

        return `${helper ? uniqueHelperId : ''} ${
          error ? errorDescribedById : ''
        }`.trim();
      }
      return '';
    })();

    return (
      <div
        className={classNames({
          'ps-select': true,
          'ps-select-error': !!error,
          [`${className}`]: className,
        })}
      >
        {label && (
          <Label
            disabled={disabled}
            htmlFor={uniqueSelectId}
          >
            {label}
          </Label>
        )}

        {helper && (
          <Helper
            disabled={disabled}
            id={uniqueHelperId}
          >
            {helper}
          </Helper>
        )}

        <div className="ps-select-select-wrapper">
          <select
            {...spreadProps}
            id={uniqueSelectId}
            aria-describedby={describedBy}
            disabled={disabled}
            defaultValue={defaultValue}
            ref={ref}
          >
            {options.map(({ label, ...restProps }) => (
              <option
                key={label}
                {...restProps}
              >
                {label}
              </option>
            ))}
          </select>

          <svg
            aria-hidden="true"
            width="12"
            height="6"
            viewBox="0 0 12 6"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M6 4.18934L1.46323 0.21967C1.1285 -0.0732233 0.585786 -0.0732233 0.251051 0.21967C-0.0836838 0.512563 -0.0836838 0.987437 0.251051 1.28033L5.39391 5.78033C5.72864 6.07322 6.27136 6.07322 6.60609 5.78033L11.7489 1.28033C12.0837 0.987437 12.0837 0.512563 11.7489 0.21967C11.4142 -0.0732233 10.8715 -0.0732233 10.5368 0.21967L6 4.18934Z" />
          </svg>
        </div>

        {error && !errorWithNoMessage && !disabled && (
          <Error
            id={uniqueErrorId}
            isErrorAnnounced={isErrorAnnounced}
          >
            {error}
          </Error>
        )}
      </div>
    );
  }
);
